import { useEffect, useMemo, useRef, useState } from "react";
import {
  Banknote,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Coins,
  LoaderCircle,
  MessageSquareText,
  ReceiptText,
  RotateCcw,
  Save,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { calculateDaily, minutesLabel, validateDailyIntegrity, yen } from "../../shared/calculations";
import type {
  AppConfig,
  BootstrapData,
  CollectionLine,
  CustomerLine,
  DailyPayload,
  ExpenseLine,
  OpenReceivable,
  ReceivableLine,
  SaveState,
  ShiftLine,
} from "../../shared/domain";
import { dailyPayloadSchema } from "../../shared/schema";
import { api } from "../api/client";
import { AddButton, ErrorSummary, LoadingBlock, MoneyInput, RowActions, Section } from "../components/ui";
import { addDays, emptyDaily, payloadFromRecord, todayInJapan, toLocalDateTime, toOffsetIso } from "../domain/form";

interface RecordMeta {
  revision: number;
  updatedAt: string;
  updatedBy: string;
  checksum: string;
}

const methodLabels: Record<string, string> = {
  CASH: "現金",
  PAYPAY: "PayPay",
  CARD: "カード",
  BANK: "振込",
  OTHER: "その他",
  UNPAID: "未払い",
  TAB: "ツケ",
  TRANSFER_DUE: "振込予定",
};

const saveStateLabels: Record<SaveState, string> = {
  idle: "未保存",
  dirty: "未保存の変更あり",
  saving: "保存中",
  saved: "保存済み",
  conflict: "更新競合",
  error: "保存失敗",
};

function newCustomer(): CustomerLine {
  return { lineId: crypto.randomUUID(), customerLabel: "", amount: 0, memo: "" };
}
function newExpense(): ExpenseLine {
  return { lineId: crypto.randomUUID(), vendorName: "", amount: 0, paymentMethod: "CASH", memo: "" };
}
function newReceivable(type: ReceivableLine["type"] = "TAB"): ReceivableLine {
  return {
    lineId: crypto.randomUUID(),
    receivableId: crypto.randomUUID(),
    type,
    customerLabel: "",
    amount: 0,
    description: "",
  };
}
function newShift(date: string, config: AppConfig): ShiftLine {
  const staff = config.staff.find((item) => item.active) ?? config.staff[0];
  return {
    lineId: crypto.randomUUID(),
    staffId: staff?.staffId ?? "",
    staffName: staff?.staffName ?? "",
    startedAt: `${date}T21:00:00+09:00`,
    endedAt: `${addDays(date, 1)}T02:00:00+09:00`,
    breakMinutes: 0,
    baseHourlyRateSnapshot: staff?.hourlyRate,
    paidTodayMethod: "UNPAID",
  };
}
function newCollection(receivables: OpenReceivable[]): CollectionLine {
  return {
    lineId: crypto.randomUUID(),
    collectionId: crypto.randomUUID(),
    receivableId: receivables[0]?.receivableId ?? "",
    amount: 0,
    receivedMethod: "CASH",
    description: "",
  };
}

export function DailyPage({ config, initialData, onDirtyChange }: { config: AppConfig; initialData?: BootstrapData | null; onDirtyChange: (dirty: boolean) => void }) {
  const initialDate = useMemo(() => new URLSearchParams(location.search).get("date") ?? todayInJapan(), []);
  const initial = initialData?.daily.businessDate === initialDate ? initialData : null;
  const initialRecord = initial?.daily.exists ? initial.daily : null;
  const loadedDate = useRef<string | null>(initial ? initialDate : null);
  const loadSequence = useRef(0);
  const [businessDate, setBusinessDate] = useState(initialDate);
  const [payload, setPayload] = useState<DailyPayload>(() => initialRecord ? payloadFromRecord(initialRecord) : emptyDaily(initialDate, config));
  const [meta, setMeta] = useState<RecordMeta | null>(initialRecord);
  const [saveState, setSaveState] = useState<SaveState>(initialRecord ? "saved" : "idle");
  const [loading, setLoading] = useState(!initial);
  const [message, setMessage] = useState("");
  const [openReceivables, setOpenReceivables] = useState<OpenReceivable[]>(initial?.receivables ?? []);

  const dirty = saveState === "dirty" || saveState === "error" || saveState === "conflict";

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const loadReceivables = () => api.getReceivables().then(setOpenReceivables).catch(() => setOpenReceivables([]));

  const loadDate = (date: string) => {
    loadedDate.current = date;
    const sequence = ++loadSequence.current;
    setLoading(true);
    setMessage("");
    Promise.all([api.getDaily(date), api.getReceivables()])
      .then(([response, receivables]) => {
        if (sequence !== loadSequence.current) return;
        setOpenReceivables(receivables);
        if (response.exists) {
          setPayload(payloadFromRecord(response));
          setMeta({
            revision: response.revision,
            updatedAt: response.updatedAt,
            updatedBy: response.updatedBy,
            checksum: response.checksum,
          });
          setSaveState("saved");
        } else {
          setPayload(emptyDaily(date, config));
          setMeta(null);
          setSaveState("idle");
        }
      })
      .catch((reason: Error) => {
        if (sequence !== loadSequence.current) return;
        setMessage(reason.message);
        setSaveState("error");
      })
      .finally(() => { if (sequence === loadSequence.current) setLoading(false); });
  };

  useEffect(() => {
    if (loadedDate.current !== businessDate) loadDate(businessDate);
  }, [businessDate]);

  const changeDate = (date: string) => {
    if (!date || date === businessDate) return;
    if (dirty && !window.confirm("未保存の変更があります。営業日を変更しますか？")) return;
    setBusinessDate(date);
    history.replaceState({}, "", `/?date=${date}`);
  };

  const change = (producer: (current: DailyPayload) => DailyPayload) => {
    setPayload((current) => {
      const next = producer(structuredClone(current));
      if (saveState === "saved") next.requestId = crypto.randomUUID();
      return next;
    });
    setSaveState("dirty");
    setMessage("");
  };

  const updateArray = <K extends "customers" | "receivables" | "expenses" | "shifts" | "collections">(
    key: K,
    index: number,
    value: DailyPayload[K][number],
  ) => change((draft) => {
    (draft[key] as Array<DailyPayload[K][number]>)[index] = value;
    return draft;
  });

  const removeArray = <K extends "customers" | "receivables" | "expenses" | "shifts" | "collections">(
    key: K,
    index: number,
  ) => change((draft) => {
    draft[key] = draft[key].filter((_, itemIndex) => itemIndex !== index) as DailyPayload[K];
    return draft;
  });

  const calculationResult = useMemo(() => validateDailyIntegrity(payload, config), [payload, config]);
  const calculation = calculationResult.calculation ?? calculateDaily({ ...payload, shifts: [] }, config);
  const schemaResult = useMemo(() => dailyPayloadSchema.safeParse(payload), [payload]);
  const validationMessages = useMemo(() => {
    const messages = [
      ...(schemaResult.success ? [] : schemaResult.error.issues.map((issue) => issue.message)),
      ...calculationResult.issues.map((issue) => issue.message),
    ];
    const collectionsById = new Map<string, number>();
    payload.collections.forEach((line) =>
      collectionsById.set(line.receivableId, (collectionsById.get(line.receivableId) ?? 0) + line.amount),
    );
    collectionsById.forEach((amount, receivableId) => {
      const selected = openReceivables.find((row) => row.receivableId === receivableId);
      if (selected && amount > selected.outstandingBalance) {
        messages.push(`${selected.customerLabel}への入金が未回収残高を超えています`);
      }
    });
    if (calculation.registerDifference != null && Math.abs(calculation.registerDifference) > config.registerDifferenceTolerance && !payload.note.trim()) {
      messages.push("レジ差額があるため、営業日メモに理由を入力してください");
    }
    return [...new Set(messages)];
  }, [schemaResult, calculationResult.issues, calculation.registerDifference, config.registerDifferenceTolerance, payload.collections, payload.note, openReceivables]);

  const save = async () => {
    if (validationMessages.length > 0) {
      setMessage("入力内容を確認してください");
      return;
    }
    if (meta && !window.confirm(`revision ${meta.revision} の営業日を更新します。旧版は履歴に残ります。よろしいですか？`)) return;
    setSaveState("saving");
    setMessage("");
    try {
      const response = await api.saveDaily(payload);
      if (response.revision !== response.data.revision || response.checksum !== response.data.checksum) {
        throw new Error("保存後の再読込検証に失敗しました。保存済みとして扱いません");
      }
      setPayload(payloadFromRecord(response.data));
      setMeta({
        revision: response.revision,
        updatedAt: response.savedAt,
        updatedBy: response.savedBy,
        checksum: response.checksum,
      });
      setSaveState("saved");
      setMessage(`revision ${response.revision} として保存し、再読込を確認しました`);
      loadReceivables();
    } catch (reason) {
      const error = reason as Error & { code?: string };
      setSaveState(error.code === "REVISION_CONFLICT" ? "conflict" : "error");
      setMessage(error.message || "保存に失敗しました。入力内容は画面に残っています");
    }
  };

  if (loading) return <LoadingBlock label={`${businessDate} の営業日を読み込んでいます`} />;

  return (
    <div className="page-stack daily-page">
      <header className="daily-hero">
        <div>
          <span className="eyebrow"><CalendarDays size={15} /> 日次入力</span>
          <h1>{config.storeName} の営業日</h1>
          <p>売上・経費・勤務・未収金をまとめて記録します。</p>
        </div>
        <div className="daily-hero__controls">
          <label className="date-picker">
            <span>営業日</span>
            <input type="date" value={businessDate} onChange={(event) => changeDate(event.target.value)} />
          </label>
          <div className={`save-status save-status--${saveState}`}>
            {saveState === "saving" ? <LoaderCircle className="spin" size={16} /> : saveState === "saved" ? <CheckCircle2 size={16} /> : <span className="save-status__dot" />}
            <span>{saveStateLabels[saveState]}</span>
          </div>
        </div>
      </header>

      {meta ? (
        <div className="revision-strip">
          <span><strong>revision {meta.revision}</strong></span>
          <span>最終更新 {new Date(meta.updatedAt).toLocaleString("ja-JP")}</span>
          <span>{meta.updatedBy}</span>
          <span className="checksum" title={meta.checksum}>checksum {meta.checksum.slice(0, 10)}…</span>
        </div>
      ) : (
        <div className="revision-strip revision-strip--new"><strong>新規営業日</strong><span>初回保存で revision 1 を作成します</span></div>
      )}

      {message ? <div className={`inline-alert ${saveState === "saved" ? "inline-alert--success" : "inline-alert--error"}`} role="status">{message}</div> : null}

      <Section
        title="来客明細"
        description="顧客・グループごとの利用額。少なくとも1行入力します。"
        icon={<UsersRound size={19} />}
        action={<AddButton onClick={() => change((draft) => ({ ...draft, customers: [...draft.customers, newCustomer()] }))}>来客を追加</AddButton>}
      >
        <div className="line-list">
          {payload.customers.map((row, index) => (
            <div className="line-card customer-line" key={row.lineId ?? index}>
              <span className="line-number">{String(index + 1).padStart(2, "0")}</span>
              <label className="field field--grow">
                <span className="field__label">顧客名・グループ名</span>
                <input value={row.customerLabel} maxLength={80} placeholder="例：お客さんA" onChange={(event) => updateArray("customers", index, { ...row, customerLabel: event.target.value })} />
              </label>
              <MoneyInput label="利用額" value={row.amount} min={1} required onChange={(amount) => updateArray("customers", index, { ...row, amount })} />
              <label className="field field--grow">
                <span className="field__label">メモ</span>
                <input value={row.memo ?? ""} maxLength={200} placeholder="任意" onChange={(event) => updateArray("customers", index, { ...row, memo: event.target.value })} />
              </label>
              <RowActions
                disableRemove={payload.customers.length === 1}
                onRemove={() => removeArray("customers", index)}
                onDuplicate={() => change((draft) => {
                  draft.customers.splice(index + 1, 0, { ...row, lineId: crypto.randomUUID() });
                  return draft;
                })}
              />
            </div>
          ))}
        </div>
        <div className="section-total"><span>来客利用額 合計</span><strong>{yen(calculation.salesTotal)}</strong></div>
      </Section>

      <Section title="決済内訳" description="現金売上は来客合計から自動算出します。" icon={<WalletCards size={19} />} tone="accent">
        <div className="payment-grid">
          <MoneyInput label="PayPay" value={payload.payments.paypay} onChange={(value) => change((draft) => ({ ...draft, payments: { ...draft.payments, paypay: value } }))} />
          <MoneyInput label="クレジットカード" value={payload.payments.card} onChange={(value) => change((draft) => ({ ...draft, payments: { ...draft.payments, card: value } }))} />
          <MoneyInput label="ツケ" value={payload.payments.tab} onChange={(value) => change((draft) => ({ ...draft, payments: { ...draft.payments, tab: value } }))} />
          <MoneyInput label="振込予定" value={payload.payments.transferDue} onChange={(value) => change((draft) => ({ ...draft, payments: { ...draft.payments, transferDue: value } }))} />
          <div className="calculated-field"><span>現金売上</span><strong className={calculation.cashSales < 0 ? "is-negative" : ""}>{yen(calculation.cashSales)}</strong><small>来客合計 − 現金以外</small></div>
          <div className="calculated-field calculated-field--strong"><span>当日売上</span><strong>{yen(calculation.salesTotal)}</strong><small>来客明細から算出</small></div>
        </div>
      </Section>

      <Section
        title="未収発生明細"
        description="ツケ・振込予定の対象を特定します。決済内訳との一致が必要です。"
        icon={<Coins size={19} />}
        action={<AddButton onClick={() => change((draft) => ({ ...draft, receivables: [...draft.receivables, newReceivable()] }))}>未収を追加</AddButton>}
      >
        {payload.receivables.length === 0 ? <p className="empty-inline">ツケ・振込予定がなければ入力不要です。</p> : (
          <div className="line-list">
            {payload.receivables.map((row, index) => (
              <div className="line-card receivable-line" key={row.lineId ?? index}>
                <label className="field"><span className="field__label">種別</span><select value={row.type} onChange={(event) => updateArray("receivables", index, { ...row, type: event.target.value as ReceivableLine["type"] })}><option value="TAB">ツケ</option><option value="TRANSFER_DUE">振込予定</option></select></label>
                <label className="field field--grow"><span className="field__label">顧客・対象</span><input value={row.customerLabel} maxLength={80} onChange={(event) => updateArray("receivables", index, { ...row, customerLabel: event.target.value })} /></label>
                <MoneyInput label="発生額" value={row.amount} min={1} onChange={(amount) => updateArray("receivables", index, { ...row, amount })} />
                <label className="field field--grow"><span className="field__label">内容</span><input value={row.description ?? ""} maxLength={300} placeholder="任意" onChange={(event) => updateArray("receivables", index, { ...row, description: event.target.value })} /></label>
                <RowActions onRemove={() => removeArray("receivables", index)} />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="経費明細"
        description="現金支払だけを理論レジ金から差し引きます。"
        icon={<ReceiptText size={19} />}
        action={<AddButton onClick={() => change((draft) => ({ ...draft, expenses: [...draft.expenses, newExpense()] }))}>経費を追加</AddButton>}
      >
        {payload.expenses.length === 0 ? <p className="empty-inline">本日の経費はありません。</p> : (
          <div className="line-list">
            {payload.expenses.map((row, index) => (
              <div className="line-card expense-line" key={row.lineId ?? index}>
                <label className="field field--grow"><span className="field__label">会社・ショップ名</span><input value={row.vendorName} maxLength={100} onChange={(event) => updateArray("expenses", index, { ...row, vendorName: event.target.value })} /></label>
                <MoneyInput label="金額" value={row.amount} min={1} onChange={(amount) => updateArray("expenses", index, { ...row, amount })} />
                <label className="field"><span className="field__label">支払方法</span><select value={row.paymentMethod} onChange={(event) => updateArray("expenses", index, { ...row, paymentMethod: event.target.value as ExpenseLine["paymentMethod"] })}>{config.expensePaymentMethods.map((method) => <option key={method} value={method}>{methodLabels[method]}</option>)}</select></label>
                <label className="field field--grow"><span className="field__label">用途・メモ</span><input value={row.memo ?? ""} maxLength={300} onChange={(event) => updateArray("expenses", index, { ...row, memo: event.target.value })} /></label>
                <RowActions onRemove={() => removeArray("expenses", index)} onDuplicate={() => change((draft) => { draft.expenses.splice(index + 1, 0, { ...row, lineId: crypto.randomUUID() }); return draft; })} />
              </div>
            ))}
          </div>
        )}
        <div className="section-total"><span>経費 合計</span><strong>{yen(calculation.expenseTotal)}</strong></div>
      </Section>

      <Section
        title="勤務明細"
        description="1分単位で通常・深夜時間と支給見込額を計算します。"
        icon={<UsersRound size={19} />}
        action={<AddButton onClick={() => change((draft) => ({ ...draft, shifts: [...draft.shifts, newShift(businessDate, config)] }))}>勤務を追加</AddButton>}
      >
        {payload.shifts.length === 0 ? <p className="empty-inline">本日の勤務明細はありません。</p> : (
          <div className="line-list">
            {payload.shifts.map((row, index) => {
              const staff = config.staff.find((item) => item.staffId === row.staffId);
              const shiftCalculation = calculation.shifts[index];
              return (
                <div className="line-card shift-line" key={row.lineId ?? index}>
                  <label className="field"><span className="field__label">氏名</span><select value={row.staffId} onChange={(event) => { const nextStaff = config.staff.find((item) => item.staffId === event.target.value); updateArray("shifts", index, { ...row, staffId: event.target.value, staffName: nextStaff?.staffName, baseHourlyRateSnapshot: nextStaff?.hourlyRate }); }}>{config.staff.filter((item) => item.active || item.staffId === row.staffId).map((item) => <option value={item.staffId} key={item.staffId}>{item.staffName}</option>)}</select></label>
                  <label className="field"><span className="field__label">開始</span><input type="datetime-local" value={toLocalDateTime(row.startedAt)} onChange={(event) => updateArray("shifts", index, { ...row, startedAt: toOffsetIso(event.target.value) })} /></label>
                  <label className="field"><span className="field__label">終了</span><input type="datetime-local" value={toLocalDateTime(row.endedAt)} onChange={(event) => {
                    const localValue = event.target.value;
                    let endedAt = toOffsetIso(localValue);
                    if (endedAt && new Date(endedAt).getTime() <= new Date(row.startedAt).getTime()) {
                      endedAt = `${addDays(row.startedAt.slice(0, 10), 1)}T${localValue.slice(11)}:00+09:00`;
                    }
                    updateArray("shifts", index, { ...row, endedAt });
                  }} /></label>
                  <label className="field"><span className="field__label">休憩（分）</span><input type="number" min="0" step="1" value={row.breakMinutes} onChange={(event) => updateArray("shifts", index, { ...row, breakMinutes: Number(event.target.value) })} /></label>
                  <label className="field"><span className="field__label">当日支払</span><select value={row.paidTodayMethod} onChange={(event) => updateArray("shifts", index, { ...row, paidTodayMethod: event.target.value as ShiftLine["paidTodayMethod"] })}><option value="UNPAID">未払い</option><option value="CASH">現金</option><option value="OTHER">その他</option></select></label>
                  <div className="shift-result">
                    <span>時給 {yen(row.baseHourlyRateSnapshot ?? staff?.hourlyRate ?? 0)}</span>
                    {shiftCalculation ? <><span>通常 {minutesLabel(shiftCalculation.normalMinutes)}</span><span>深夜 {minutesLabel(shiftCalculation.lateNightMinutes)}</span><strong>{yen(shiftCalculation.totalWage)}</strong></> : <span className="is-negative">時刻を確認</span>}
                  </div>
                  <RowActions onRemove={() => removeArray("shifts", index)} />
                </div>
              );
            })}
          </div>
        )}
        <div className="section-total"><span>バイト代 合計</span><strong>{yen(calculation.wageTotal)}</strong></div>
      </Section>

      <Section
        title="後日入金"
        description="過去の未収残高を減らします。新しい売上には加算しません。"
        icon={<Banknote size={19} />}
        action={<AddButton onClick={() => change((draft) => ({ ...draft, collections: [...draft.collections, newCollection(openReceivables)] }))}>入金を追加</AddButton>}
      >
        {payload.collections.length === 0 ? <p className="empty-inline">後日入金がなければ入力不要です。</p> : openReceivables.length === 0 ? <div className="inline-alert inline-alert--warning">回収可能な未収明細がありません。</div> : (
          <div className="line-list">
            {payload.collections.map((row, index) => {
              const selected = openReceivables.find((item) => item.receivableId === row.receivableId);
              return (
                <div className="line-card collection-line" key={row.lineId ?? index}>
                  <label className="field field--wide"><span className="field__label">回収対象</span><select value={row.receivableId} onChange={(event) => updateArray("collections", index, { ...row, receivableId: event.target.value })}>{openReceivables.map((item) => <option value={item.receivableId} key={item.receivableId}>{item.businessDate} · {item.customerLabel} · 残 {yen(item.outstandingBalance)}</option>)}</select>{selected ? <small>{methodLabels[selected.type]} / 元金 {yen(selected.originalAmount)}</small> : null}</label>
                  <MoneyInput label="入金額" value={row.amount} min={1} hint={selected ? `残高 ${yen(selected.outstandingBalance)}` : undefined} onChange={(amount) => updateArray("collections", index, { ...row, amount })} />
                  <label className="field"><span className="field__label">受取方法</span><select value={row.receivedMethod} onChange={(event) => updateArray("collections", index, { ...row, receivedMethod: event.target.value as CollectionLine["receivedMethod"] })}>{config.collectionMethods.map((method) => <option key={method} value={method}>{methodLabels[method]}</option>)}</select></label>
                  <label className="field field--grow"><span className="field__label">メモ</span><input value={row.description ?? ""} maxLength={300} onChange={(event) => updateArray("collections", index, { ...row, description: event.target.value })} /></label>
                  <RowActions onRemove={() => removeArray("collections", index)} />
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <div className="two-column-grid">
        <Section title="レジ確認（任意）" description="未入力でも保存できます。両方の金額を入力すると差額を計算します。" icon={<ClipboardCheck size={19} />}>
          <div className="register-grid">
            <MoneyInput label="営業開始時レジ金（任意）" allowEmpty value={payload.openingRegisterCash} onChange={(value) => change((draft) => ({ ...draft, openingRegisterCash: value }))} />
            <MoneyInput label="閉店時レジ実査額（任意）" allowEmpty value={payload.closingRegisterCashActual} onChange={(value) => change((draft) => ({ ...draft, closingRegisterCashActual: value }))} />
            <div className="calculated-field"><span>理論閉店レジ金</span><strong>{calculation.expectedRegisterCash == null ? "未計算" : yen(calculation.expectedRegisterCash)}</strong></div>
            <div className={`calculated-field calculated-field--difference ${calculation.registerDifference != null && calculation.registerDifference !== 0 ? "has-difference" : ""}`}><span>レジ差額</span><strong>{calculation.registerDifference == null ? "未確認" : yen(calculation.registerDifference)}</strong><small>実査額 − 理論額</small></div>
          </div>
        </Section>

        <Section title="日次結果" description="保存時もサーバー側で再計算します。" icon={<ClipboardCheck size={19} />}>
          <dl className="result-list">
            <div><dt>当日売上</dt><dd>{yen(calculation.salesTotal)}</dd></div>
            <div><dt>現金売上</dt><dd>{yen(calculation.cashSales)}</dd></div>
            <div><dt>当日経費</dt><dd>− {yen(calculation.expenseTotal)}</dd></div>
            <div><dt>当日バイト代</dt><dd>− {yen(calculation.wageTotal)}</dd></div>
            <div className="result-list__profit"><dt>当日利益</dt><dd className={calculation.dailyProfit < 0 ? "is-negative" : ""}>{yen(calculation.dailyProfit)}</dd></div>
          </dl>
        </Section>
      </div>

      <Section title="備考・保存" description="レジ差額がある場合は理由を記録してください。" icon={<MessageSquareText size={19} />}>
        <label className="field"><span className="field__label">営業日メモ</span><textarea rows={4} maxLength={1000} value={payload.note} placeholder="申し送り、レジ差額の理由など" onChange={(event) => change((draft) => ({ ...draft, note: event.target.value }))} /></label>
        <ErrorSummary messages={validationMessages} />
        <div className="save-panel">
          <div>
            <strong>{meta ? `revision ${meta.revision + 1} として更新` : "revision 1 として新規保存"}</strong>
            <span>保存後にスプレッドシートを再読込して一致を確認します。</span>
          </div>
          {saveState === "conflict" ? <button type="button" className="button button--quiet" onClick={() => loadDate(businessDate)}><RotateCcw size={17} /> 最新を再読込</button> : null}
          <button type="button" className="button button--primary button--save" disabled={saveState === "saving" || validationMessages.length > 0 || (saveState !== "dirty" && saveState !== "error" && saveState !== "conflict" && !!meta)} onClick={save}>
            {saveState === "saving" ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />}
            {saveState === "saving" ? "保存しています…" : meta ? "この営業日を更新" : "営業日を保存"}
          </button>
        </div>
      </Section>
    </div>
  );
}
